import { useState, useEffect, useRef } from 'react';

import { SENDER_TYPE, Message } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useMessageActions } from '@backrs_ui/grow-ui/src/components/Lubav/hooks/useMessageActions';
import { DoneMessageWithIds } from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import {
  SolutionSessionMessage,
  MESSAGE_MODE,
  MainResponse,
  ResumeBuilderStep,
  LUBAV_LLM,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  setCollectedData,
  setIsLoadingResources,
  setMainResponseData,
  setStreamingComplete,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  addSolutionSessionMessage,
  setSolutionSessionMessages,
  setSessionId,
  setIsLoading,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { useGetNewSessionIdForMessageEditMutation as useGetOfficeHoursNewSessionIdForMessageEditMutation } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { selectIsSharedSessionActive } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import {
  setChatMode,
  setUserMessage,
  setInputBoxMessage,
  setUserInput,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';
import {
  useGetResponseMutation,
  useSendChatMessageMutation,
  useGetNewSessionIdForMessageEditMutation,
  useSendQAMessageMutation,
  useGetCollectedDataMutation,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.api';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, SxProps, Theme } from '@mui/material';
import { jsonrepair } from 'jsonrepair';

import { Question } from './Questions/Question';
import {
  abortResourceLoading,
  createResourceAbortController,
  isResourceStreamingStopped,
  resetResourceAbortController,
} from './resourceAbortController';
import { ChatBoxContainer } from '../Lubav/ChatBoxContainer';
import { EnterButton } from '../Lubav/EnterButton';
import { AIMessageActionsButton } from '../Lubav/MessageActionButtons';

const QUESTION_STRINGS = appStrings.modules.solutionSessions.questions;

export const ChatBox = () => {
  const activeSession = useAppSelector(getActiveLubavSession);
  const isShared = useAppSelector(selectIsSharedSessionActive);
  const [getResponseMutation] = useGetResponseMutation();
  const [sendChatMessageMutation] = useSendChatMessageMutation();
  const [getNewSessionIdForMessageEditMutation] = useGetNewSessionIdForMessageEditMutation();
  const [sendQAMessageMutation] = useSendQAMessageMutation();
  const [getCollectedDataMutation] = useGetCollectedDataMutation();
  const [getOfficeHoursNewSessionIdForMessageEditMutation] =
    useGetOfficeHoursNewSessionIdForMessageEditMutation();

  const { solutionSessionMessages, loading: isTyping } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const {
    loading: isLoadingResources,
    streamingComplete,
    boxData,
  } = useAppSelector((state) => getSessionState(state, activeSession).boxData);
  const hasMainResponse = boxData.mainResponse !== undefined;
  const isResourcesStreaming = hasMainResponse && !streamingComplete;
  const { chatMode, inputBoxMessage, userInput } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  const userId = useAppSelector(selectUserId);

  const dispatch = useAppDispatch();

  const { handleCopy, handleThumbsUp, handleThumbsDown, handleBookmark } = useMessageActions({
    activeSession,
  });

  const isEditingRef = useRef(false);
  const messageContentRefs = useRef(new Map<number, HTMLDivElement | null>());
  const abortControllerRef = useRef<AbortController | null>(null);
  const stoppedStreamingRef = useRef(false);

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const [streamingContent, setStreamingContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);

  const lastMessage = solutionSessionMessages[solutionSessionMessages.length - 1];

  useEffect(() => {
    const initializeChat = async () => {
      if (solutionSessionMessages.length === 0 && sessionId) {
        dispatch(createSessionAction(setChatMode, activeSession)(false));
        sendQAMessage('<START>', undefined);
      }
    };

    initializeChat();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionSessionMessages.length, sessionId]);

  useEffect(() => {
    return () => {
      stoppedStreamingRef.current = true;
      abortResourceLoading();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsStreaming(false);
      }
    };
  }, [sessionId, activeSession]);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const handleEditSubmitChat = async (queryId: string | undefined, newText: string) => {
    if (!queryId || !sessionId) {
      return;
    }

    try {
      isEditingRef.current = true;
      setEditingMessageId(null);
      const messageIndex = solutionSessionMessages.findIndex((msg) => msg.id === queryId);

      const updatedMessages: SolutionSessionMessage[] = [
        ...solutionSessionMessages.slice(0, messageIndex),
      ];

      dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(updatedMessages));

      if (chatMode) {
        const response = await getOfficeHoursNewSessionIdForMessageEditMutation({
          session_id: sessionId,
          query_id: queryId,
        }).unwrap();
        const newSessionId = response.session_id;

        dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));

        await sendChatMessage(newText, undefined, updatedMessages, String(newSessionId));
      } else {
        await sendQAMessage(newText, newText);
      }

      isEditingRef.current = false;
    } catch (error) {
      console.error('Error editing message:', error);
      isEditingRef.current = false;
    }
  };

  const handleSetMainResponseData = (mainResponse: MainResponse) => {
    dispatch(createSessionAction(setMainResponseData, activeSession)(mainResponse));
  };

  async function getResponse({ withFinalMessage = true }: { withFinalMessage?: boolean } = {}) {
    if (!activeSession || !sessionId || !userId) {
      return null;
    }

    const abortController = createResourceAbortController();

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(true));

    try {
      const getResponsePromise = getResponseMutation({
        session_id: sessionId,
        llm: LUBAV_LLM,
        solution_session: activeSession,
        with_final_message: withFinalMessage,
        signal: abortController.signal,
        onChunk: (chunk) => {
          if (isResourceStreamingStopped()) return;

          try {
            const fixedJson = jsonrepair(chunk);
            const parsedResponse = JSON.parse(fixedJson);

            handleSetMainResponseData(parsedResponse.output);

            setTimeout(() => {
              dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
            }, 3000);
          } catch {
            // do nothing
          }
        },
      }).unwrap();

      // await getResponse before adding the final message
      const sessionResponse = await getResponsePromise;

      if (withFinalMessage && !isResourceStreamingStopped()) {
        dispatch(
          createSessionAction(
            addSolutionSessionMessage,
            activeSession,
          )({
            text: sessionResponse.output.final_message?.final_message as string,
            type: 'final_message',
            sender: SENDER_TYPE.AI,
            suggestedPrompts: sessionResponse.output.final_message?.suggested_prompts || [],
            ...(activeSession.startsWith('resume_builder') && {
              resume_builder_step: 'polish',
            }),
          }),
        );
      }

      resetResourceAbortController();
    } catch (error) {
      resetResourceAbortController();

      if (!abortController.signal.aborted) {
        dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
        dispatch(createSessionAction(setStreamingComplete, activeSession)(true));

        // for now we assume an overload error if we get any error
        // TODO: handle errors on a case by case basis
        dispatch(
          createSessionAction(
            addSolutionSessionMessage,
            activeSession,
          )({
            text: appStrings.modules.chatBox.overloadErr,
            sender: SENDER_TYPE.AI,
          }),
        );
        console.error('Error fetching response:', error);
      }
    } finally {
      dispatch(createSessionAction(setStreamingComplete, activeSession)(true));
      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
    }
  }

  const getNLastAIMessage = (messages: SolutionSessionMessage[], n: number) => {
    const AIMessages = messages.filter((m) => m.sender === SENDER_TYPE.AI);

    return AIMessages[AIMessages.length - n];
  };

  const shouldUpdateResponse = (
    previousAiMessage: SolutionSessionMessage,
    currentAiMessage: SolutionSessionMessage,
    userMessage: SolutionSessionMessage,
    currentResumeBuilderStep?: ResumeBuilderStep,
  ) => {
    if (
      previousAiMessage?.update_on_submit &&
      currentAiMessage?.response_is_valid &&
      userMessage?.text?.trim() !== '' &&
      userMessage?.text !== QUESTION_STRINGS.skipThisQuestion
    ) {
      return true;
    }

    // for resume builder, if the step has changed, return true
    if (activeSession.startsWith('resume_builder')) {
      if (
        previousAiMessage?.resume_builder_step &&
        currentResumeBuilderStep &&
        previousAiMessage?.resume_builder_step !== currentResumeBuilderStep &&
        currentResumeBuilderStep !== 'polish' // on polish step, we don't need to update since the session is over, it will do it automatically
      ) {
        return true;
      }
    }

    return false;
  };

  async function sendQAMessage(input: string, message?: string) {
    if (
      !activeSession ||
      !typeGuardForSolutionSessionId(activeSession) ||
      isLoadingResources ||
      !sessionId
    ) {
      return null;
    }

    let newSessionId: number | null = null;

    // Determine the current messages based on whether we are editing.
    const currentMessages = (() => {
      if (editingMessageId && !chatMode) {
        // Find the index of the message being edited.
        const messageIndex = solutionSessionMessages.findIndex(
          (msg) => msg.id === editingMessageId,
        );

        // Remove the edited message and everything after it.
        return solutionSessionMessages.slice(0, messageIndex);
      }

      return solutionSessionMessages;
    })();

    // If we are editing, perform the async actions related to that.
    if (editingMessageId && !chatMode) {
      isEditingRef.current = true;
      // Update state with the pruned list.
      dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(currentMessages));
      // Request a new session ID for this edited message.
      if (!sessionId) {
        return null;
      }

      const response = await getNewSessionIdForMessageEditMutation({
        session_id: sessionId,
        query_id: editingMessageId,
      }).unwrap();

      newSessionId = response.session_id;

      dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));
      setEditingMessageId(null);
    }

    // If message is an array, join it into a string.
    if (Array.isArray(message)) {
      message = message.join(', ');
    }

    // Create a new messages array that includes the user message optimistically.
    const optimisticMessages = message
      ? [
          ...currentMessages,
          {
            text: message,
            sender: SENDER_TYPE.USER,
            mode: MESSAGE_MODE.QA,
          },
        ]
      : currentMessages;

    // Update the UI with the optimistic message.
    dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(optimisticMessages));

    dispatch(createSessionAction(setIsLoading, activeSession)(true));
    dispatch(setUserMessage(''));

    const lastAIMessage = getNLastAIMessage(optimisticMessages, 1);
    const questionKey = lastAIMessage?.field || lastAIMessage?.questionField;

    const sessionIdToUse = newSessionId || sessionId;

    if (!sessionIdToUse) {
      return null;
    }

    try {
      const mainResponse = await sendQAMessageMutation({
        text: input,
        session_id: sessionIdToUse,
        question_key: questionKey,
        llm: LUBAV_LLM,
        solution_session: activeSession,
      }).unwrap();

      const question = mainResponse.question;
      const { query_id, response_id } = mainResponse;

      if (mainResponse.finished) {
        dispatch(createSessionAction(setChatMode, activeSession)(true));
        if (!isLoadingResources) {
          getResponse({ withFinalMessage: true });
        }
      }

      dispatch(createSessionAction(setIsLoading, activeSession)(false));

      const userMessage: SolutionSessionMessage = {
        text: message as string,
        sender: SENDER_TYPE.USER,
        id: query_id,
        mode: MESSAGE_MODE.QA,
      };

      const questionProps = question
        ? {
            question: question.text,
            questionField: question.field,
            ...(question.type && { type: question.type }),
            options: question.options,
            include_freeform: question.include_freeform,
            required: question.required,
            user_selection_options: question.user_selection_options,
            placeholder: question.placeholder,
            update_on_submit: question.update_on_submit,
            ...(question.resume_builder_step && {
              resume_builder_step: question.resume_builder_step,
            }),
            additionalText: question.additional_text,
          }
        : {};

      const aiMessage: SolutionSessionMessage = {
        text: mainResponse.text,
        sender: SENDER_TYPE.AI,
        id: response_id,
        mode: MESSAGE_MODE.QA,
        validation_feedback: mainResponse.validation_feedback,
        response_is_valid: mainResponse.response_is_valid,
        ...questionProps,
      };

      // Remove the temporary user message (assumed to be the last one) and add the finalized one plus the AI message.
      dispatch(
        createSessionAction(
          setSolutionSessionMessages,
          activeSession,
        )([...optimisticMessages.slice(0, -1), ...(message ? [userMessage] : []), aiMessage]),
      );

      const shouldUpdate = shouldUpdateResponse(
        getNLastAIMessage(optimisticMessages, 1),
        aiMessage,
        userMessage,
        question?.resume_builder_step,
      );

      if (shouldUpdate && !isLoadingResources) {
        await getResponse({ withFinalMessage: false });
      }

      const collectedData = await getCollectedDataMutation({
        session_id: sessionIdToUse,
      }).unwrap();

      dispatch(createSessionAction(setCollectedData, activeSession)(collectedData));
    } catch (error) {
      dispatch(createSessionAction(setIsLoading, activeSession)(false));

      // Use currentMessages (or optimisticMessages) to ensure we're not using stale data.
      dispatch(
        createSessionAction(
          addSolutionSessionMessage,
          activeSession,
        )({
          text: appStrings.modules.chatBox.overloadErr,
          sender: SENDER_TYPE.AI,
        }),
      );
      console.error('Error fetching response:', error);
    }

    isEditingRef.current = false;
  }

  const handleDeltaMeta = () => {
    if (stoppedStreamingRef.current) return;

    setStreamingContent('');
    setIsStreaming(true);
  };

  const handleDeltaContent = (accumulated: string) => {
    if (stoppedStreamingRef.current) return;

    setStreamingContent(accumulated);
  };

  const createDoneHandler = (pendingMessage: string, messagesToUse: SolutionSessionMessage[]) => {
    return (done: DoneMessageWithIds) => {
      const updatedMessages: SolutionSessionMessage[] = [
        ...messagesToUse,
        {
          text: pendingMessage,
          sender: SENDER_TYPE.USER,
          id: String(done.query_id),
          mode: MESSAGE_MODE.CHAT,
        },
        {
          text: done.response,
          sender: SENDER_TYPE.AI,
          id: String(done.response_id),
          mode: MESSAGE_MODE.CHAT,
          ask_to_regenerate: done.ask_to_regenerate,
          ...(activeSession.startsWith('resume_builder') && {
            resume_builder_step: undefined,
          }),
        },
      ];

      dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(updatedMessages));
      dispatch(createSessionAction(setIsLoading, activeSession)(false));

      setStreamingContent('');
      setIsStreaming(false);
    };
  };

  async function sendChatMessage(
    input: string,
    _?: string,
    currentMessageList?: SolutionSessionMessage[],
    newSessionId?: string,
  ) {
    if (!activeSession) {
      return null;
    }

    const messagesToUse = currentMessageList || solutionSessionMessages;

    const sessionIdToUse = newSessionId ? Number(newSessionId) : sessionId;

    if (!sessionIdToUse) {
      return null;
    }

    const abortController = new AbortController();

    abortControllerRef.current = abortController;
    stoppedStreamingRef.current = false;

    const handleDone = createDoneHandler(input, messagesToUse);

    const updatedMessages: SolutionSessionMessage[] = input
      ? [...messagesToUse, { text: input, sender: SENDER_TYPE.USER }]
      : messagesToUse;

    dispatch(createSessionAction(setSolutionSessionMessages, activeSession)(updatedMessages));

    dispatch(createSessionAction(setIsLoading, activeSession)(true));
    dispatch(setUserMessage(''));

    try {
      await sendChatMessageMutation({
        text: input,
        session_id: sessionIdToUse,
        llm: LUBAV_LLM,
        solution_session: activeSession,
        signal: abortController.signal,
        onMeta: handleDeltaMeta,
        onDelta: handleDeltaContent,
        onDone: handleDone,
      }).unwrap();

      abortControllerRef.current = null;
    } catch (error) {
      abortControllerRef.current = null;

      if (!abortController.signal.aborted) {
        dispatch(createSessionAction(setIsLoading, activeSession)(false));
        setIsStreaming(false);
        setStreamingContent('');

        // for now we assume an overload error if we get any error
        // TODO: handle errors on a case by case basis
        dispatch(
          createSessionAction(
            setSolutionSessionMessages,
            activeSession,
          )([
            ...solutionSessionMessages,
            {
              text: appStrings.modules.chatBox.overloadErr,
              sender: SENDER_TYPE.AI,
            },
          ]),
        );
        console.error('Error fetching response:', error);
      }
    }
  }

  const handleEdit = async (queryId: string | undefined) => {
    if (!queryId) {
      return;
    }

    try {
      // if we're already editing this message, cancel the edit
      if (editingMessageId === queryId) {
        setEditingMessageId(null);
        setEditingText('');

        return;
      }

      const messageIndex = solutionSessionMessages.findIndex((msg) => msg.id === queryId);
      const messageText = solutionSessionMessages[messageIndex].text;

      setEditingMessageId(queryId);
      setEditingText(messageText || '');
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const handleStopStreaming = () => {
    stoppedStreamingRef.current = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (streamingContent.trim()) {
      dispatch(
        createSessionAction(
          addSolutionSessionMessage,
          activeSession,
        )({
          text: streamingContent,
          sender: SENDER_TYPE.AI,
          mode: MESSAGE_MODE.CHAT,
        }),
      );
      setStreamingContent('');
    }

    dispatch(createSessionAction(setIsLoading, activeSession)(false));
    setIsStreaming(false);

    abortResourceLoading();
    dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
    dispatch(createSessionAction(setStreamingComplete, activeSession)(true));
  };

  const handleInputOnChange = (value: string) => {
    dispatch(createSessionAction(setInputBoxMessage, activeSession)(value));
    dispatch(createSessionAction(setUserInput, activeSession)({ type: 'SINGLE_INPUT', value }));
  };

  const handleInputSend = async () => {
    if (!inputBoxMessage.trim() || userInput?.type !== 'SINGLE_INPUT') return;
    if (isTyping || isLoadingResources || isStreaming) return;

    dispatch(createSessionAction(setInputBoxMessage, activeSession)(''));
    dispatch(createSessionAction(setUserInput, activeSession)(undefined));

    if (chatMode) {
      await sendChatMessage(userInput.value);
      if (activeSession.startsWith('resume_builder')) {
        getResponse({ withFinalMessage: false });
      }
    } else {
      sendQAMessage(userInput.value, inputBoxMessage);
    }
  };

  // helper function to determine if the message is being edited
  const isEditingMessage = (msg: SolutionSessionMessage, index: number) => {
    const type = solutionSessionMessages[index - 1]?.type;

    if (!type) {
      return false;
    }

    return (
      editingMessageId === msg.id &&
      (solutionSessionMessages[index - 1]?.include_freeform ||
        ['numeric', 'freeform'].includes(type) ||
        chatMode)
    );
  };

  // helper function to determine if the question should be shown
  const shouldShowQuestion = (msg: SolutionSessionMessage, index: number) => {
    return (
      (msg.sender === SENDER_TYPE.AI && index === solutionSessionMessages.length - 1) ||
      solutionSessionMessages[index + 1]?.id === editingMessageId
    );
  };

  // helper function to determine if the suggested prompts should be shown
  const shouldShowSuggestedPrompts = () => {
    return (
      chatMode && lastMessage?.sender === SENDER_TYPE.AI && lastMessage?.type === 'final_message'
    );
  };

  // TODO: Once we have the suggested prompts per session, implement it here
  const suggestedPromptsComponent = undefined;
  // const suggestedPromptsComponent = shouldShowSuggestedPrompts() ? (
  //   <SuggestedPrompts
  //     prompts={finalMessages[activeSession].followups.map((prompt) => ({
  //       display: prompt,
  //     }))}
  //     onSelect={(params: { message: string; LLMInput?: string }) =>
  //       sendChatMessage(params.message, params.LLMInput)
  //     }
  //   />
  // ) : undefined;

  const renderMessages = () => (
    <>
      {solutionSessionMessages?.map((msg, index) => {
        // Create the component that will be passed to Message
        const messageComponent = isShared ? null : (
          <>
            {msg.ask_to_regenerate &&
              index === solutionSessionMessages.length - 1 &&
              !isLoadingResources && (
                <EnterButton onClick={() => getResponse()} label="Regenerate" />
              )}
            {shouldShowQuestion(msg, index) && userId && msg.type && (
              <Question
                type={msg.type}
                sendQAMessage={sendQAMessage}
                required={msg.required || false}
                userId={userId}
                chatMode={chatMode}
                options={msg.options}
                userSelectionOptions={msg.user_selection_options}
                message={msg}
              />
            )}
          </>
        );

        const getTextToShow = (msg: SolutionSessionMessage) => {
          // if the message is from the user, return just their message
          if (msg.sender === SENDER_TYPE.USER) {
            return msg.text;
          }

          // if the message is from the AI and the response is valid, return the message and the question
          if (msg.response_is_valid || msg.type === 'final_message' || chatMode) {
            return `${msg.text || ''} ${msg.question || ''}`;
          }

          // else, it's invalid and we show the validation feedback
          return msg.validation_feedback ?? msg.text;
        };

        return (
          <Box key={index} className="message-wrapper">
            <Message
              id={`solution-session-message-${index}`}
              text={getTextToShow(msg) || ''}
              contentRef={(el) => {
                messageContentRefs.current.set(index, el);
              }}
              additionalText={msg.additionalText}
              component={messageComponent}
              sender={msg.sender}
              actionButtons={
                msg.sender === SENDER_TYPE.AI && !isShared ? (
                  <Box sx={messageActionsContainer}>
                    <AIMessageActionsButton
                      onCopy={() =>
                        handleCopy(getTextToShow(msg) || '', messageContentRefs.current.get(index))
                      }
                      onThumbsUp={() => handleThumbsUp(msg.id || '')}
                      onThumbsDown={() => handleThumbsDown(msg.id || '')}
                      onBookmark={() => handleBookmark(getTextToShow(msg) || '')}
                    />
                  </Box>
                ) : undefined
              }
              onEdit={isShared ? undefined : () => handleEdit(msg.id)}
              editingText={editingText}
              onEditingTextChange={(text) => setEditingText(text)}
              isEditing={!isShared && isEditingMessage(msg, index)}
              onEditSubmit={() => handleEditSubmitChat(msg.id, editingText)}
              onEditCancel={() => {
                setEditingMessageId(null);
                setEditingText('');
              }}
            />
          </Box>
        );
      })}
      {isStreaming && streamingContent && (
        <Box className="message-wrapper">
          <Message
            id="solution-session-message-streaming"
            text={streamingContent}
            sender={SENDER_TYPE.AI}
            component={null}
          />
        </Box>
      )}
    </>
  );

  const scrollKey = `${solutionSessionMessages.length}`;

  return (
    <ChatBoxContainer
      isTyping={isTyping && !streamingContent}
      scrollKey={scrollKey}
      inputProps={{
        value: inputBoxMessage,
        onChange: handleInputOnChange,
        onSend: handleInputSend,
        onStop: handleStopStreaming,
        disabled: lastMessage?.type !== 'freeform' && lastMessage?.type !== 'numeric' && !chatMode,
        sendButtonDisabled: isTyping || isLoadingResources || isStreaming || isResourcesStreaming,
        isStreaming: isStreaming || isLoadingResources || isResourcesStreaming,
        isNumeric: lastMessage?.type === 'numeric',
        placeholder: lastMessage?.placeholder,
      }}
      suggestedPrompts={suggestedPromptsComponent}
      showSuggestedPrompts={shouldShowSuggestedPrompts() && !isShared}
      stickySuggestedPrompts={false}
      readOnly={isShared}
    >
      {renderMessages()}
    </ChatBoxContainer>
  );
};

const messageActionsContainer: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  marginBottom: backrs.spacing['spacing-md'],
});
